/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Analytics › People (contract section 5): the top 200 people of the window from GET /v1/admin/usage/people with the lens
 * (sessions, events, last seen, top screen, surfaces; counts only, never content), their directory names from
 * GET /v1/admin/people where the service knows one, and an eye on each row that marks or unmarks the person internal
 * (PUT /v1/admin/usage/audience, source `admin`). The Internal people panel beside it lists everyone marked, with why.
 */
import { useState } from 'react';
import { Eye, EyeOff, Users } from 'lucide-react';
import { api } from '../../lib/api';
import { PEOPLE_LIMIT } from '../../lib/people';
import { directoryNames, internalSet, nameLabel, normalizeAudience, normalizePeople, personLabel, surfaceLabel, type AudienceView, type PersonRow } from '../../lib/usage';
import { Button, Callout, EmptyState, Panel, Stack, Table, Tag, type Column } from '../../ui';
import { count, dateTime } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { AudiencePanel } from './AudiencePanel';
import { UsageError, usageFailure, type SectionProps } from './parts';

export function UsagePeople({ lens, query, lensKey, canEdit }: SectionProps & { canEdit: boolean }) {
  const people = useLoad(() => api.usagePeople(query).then(normalizePeople), `people|${lensKey}`);
  const audience = useLoad(() => api.usageAudience().then(normalizeAudience), 'audience');
  // The directory names are a convenience: a refused or failed read (an auditor cannot search people) leaves the accounts.
  const names = useLoad(() => api.people('', PEOPLE_LIMIT).then(directoryNames, () => new Map<string, string>()), 'directory-names');
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const known = names.load.state === 'ready' ? names.load.data : new Map<string, string>();
  const marked = internalSet(audience.load.state === 'ready' ? audience.load.data : null);
  const audienceView: AudienceView | null = audience.load.state === 'ready' ? audience.load.data : null;

  // After any audience write both lists are read again: the people list follows the lens, so a newly marked person leaves
  // General users (and a newly unmarked one comes back) without a page reload.
  const reloadBoth = () => {
    audience.reload();
    people.reload();
  };

  const mark = async (upn: string, internal: boolean) => {
    setBusy(upn);
    setFailure(null);
    try {
      await api.setUsageAudience([upn], internal);
      reloadBoth();
    } catch (error: unknown) {
      setFailure(usageFailure(error, `${internal ? 'Marking' : 'Unmarking'} ${upn} did not complete.`));
    } finally {
      setBusy(null);
    }
  };

  const columns: Array<Column<PersonRow>> = [
    {
      key: 'person', header: 'Person',
      cell: (row) => {
        const name = personLabel(row.upn, known);
        return (
          <span className="ui-cell-stack">
            <span className="ui-cell-title">{name}{marked.has(row.upn) ? <Tag mono={false}>internal</Tag> : null}</span>
            {name !== row.upn ? <span className="ui-cell-sub is-mono">{row.upn}</span> : null}
          </span>
        );
      },
    },
    { key: 'sessions', header: 'Sessions', numeric: true, cell: (row) => count(row.sessions) },
    { key: 'events', header: 'Events', numeric: true, cell: (row) => count(row.events) },
    { key: 'lastSeen', header: 'Last seen', hideOnPhone: true, cell: (row) => dateTime(row.lastSeen) },
    { key: 'topScreen', header: 'Top screen', hideOnPhone: true, cell: (row) => (row.topScreen ? nameLabel(row.topScreen) : '—') },
    { key: 'surfaces', header: 'Surfaces', hideOnPhone: true, cell: (row) => <span className="ui-row-tags">{row.surfaces.map((surface) => <Tag key={surface} mono={false}>{surfaceLabel(surface)}</Tag>)}</span> },
    ...(canEdit ? [{
      key: 'mark', header: 'Internal', align: 'end' as const, width: '64px',
      cell: (row: PersonRow) => {
        const internal = marked.has(row.upn);
        return (
          <Button
            variant="ghost"
            size="sm"
            icon={internal ? EyeOff : Eye}
            aria-label={internal ? `Unmark ${row.upn} internal` : `Mark ${row.upn} internal`}
            aria-pressed={internal}
            title={internal ? 'Marked internal: left out of General users. Click to unmark.' : 'Mark internal: leave this person out of General users.'}
            busy={busy === row.upn}
            disabled={audience.load.state !== 'ready'}
            onClick={() => void mark(row.upn, !internal)}
          />
        );
      },
    }] : []),
  ];

  return (
    <Stack>
      {failure ? <Callout tone="bad" role="alert">{failure}</Callout> : null}
      <Panel flush title="People" description={`Up to 200 people with the most events in the last ${lens.window} days, with this lens. Counts only: never a message or a document.`}>
        {people.load.state === 'failed' ? <div className="ui-panel-pad"><UsageError title="People could not be loaded" error={people.load.error} onRetry={people.reload} retrying={people.reloading} /></div> : (
          <Table
            label="People"
            columns={columns}
            rows={people.load.state === 'ready' ? people.load.data : []}
            rowKey={(row) => row.upn}
            loading={people.load.state === 'loading'}
            skeletonRows={6}
            empty={<EmptyState compact icon={Users} title="No people in this window">{lens.audience === 'internal' ? 'Nobody marked internal has used Gyro in this window; the Internal people panel below lists who is marked.' : 'People appear here as they use Gyro; data starts at the client release, and the lens above may be leaving people out.'}</EmptyState>}
          />
        )}
      </Panel>
      <AudiencePanel
        view={audienceView}
        state={audience.load.state}
        error={audience.load.state === 'failed' ? audience.load.error : null}
        canEdit={canEdit}
        names={known}
        onRetry={audience.reload}
        onChanged={reloadBoth}
      />
    </Stack>
  );
}
